import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Host-controlled crop for shared content.
 *
 * The host frames their own share once and applies it; every student then sees
 * exactly that region filling their screen. Framings can be saved under their
 * own name so the next class is a single tap.
 *
 * A crop is stored as fractions of the *displayed* (letterboxed) image:
 * { x, y, w, h } in 0..1 — resolution independent, so a phone and a laptop
 * render identical framing.
 */

const FULL = { x: 0, y: 0, w: 1, h: 1 };
const STORE_KEY = "udyog_saved_crops";

export const isFullCrop = (c) =>
  !c || (c.x <= 0.002 && c.y <= 0.002 && c.w >= 0.998 && c.h >= 0.998);

export function loadSavedCrops() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((c) => c?.crop && c?.name).slice(0, 8) : [];
  } catch (_) {
    return [];
  }
}

function writeSavedCrops(list) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(0, 8)));
  } catch (_) {}
  return list.slice(0, 8);
}

export function addSavedCrop(name, crop) {
  const list = loadSavedCrops().filter((c) => c.name.toLowerCase() !== name.toLowerCase());
  return writeSavedCrops([{ id: `${Date.now()}`, name, crop }, ...list]);
}

export function removeSavedCrop(id) {
  return writeSavedCrops(loadSavedCrops().filter((c) => c.id !== id));
}

const clamp01 = (v) => Math.min(1, Math.max(0, v));

/** Where the letterboxed video actually sits inside its box. */
function imageRect(W, H, aspect) {
  if (!W || !H || !aspect) return { ox: 0, oy: 0, dw: W, dh: H };
  if (aspect > W / H) {
    const dw = W;
    const dh = W / aspect;
    return { ox: 0, oy: (H - dh) / 2, dw, dh };
  }
  const dh = H;
  const dw = H * aspect;
  return { ox: (W - dw) / 2, oy: 0, dw, dh };
}

function useBoxSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/**
 * Renders the shared video, zoomed and panned so only the host's crop shows.
 * `onMeta` reports the intrinsic aspect of the share and the aspect of what is
 * visible after cropping (the stage uses it to keep chat bubbles off the
 * content on portrait shares).
 */
export const CropFrame = ({ crop, onMeta, children, className = "absolute inset-0" }) => {
  const boxRef = useRef(null);
  const { w: W, h: H } = useBoxSize(boxRef);
  const [aspect, setAspect] = useState(0);
  const metaRef = useRef(onMeta);
  useEffect(() => {
    metaRef.current = onMeta;
  }, [onMeta]);

  // Intrinsic size of the incoming share — LiveKit swaps resolutions mid-share
  // (simulcast, the host resizing their window), so listen to the video's own
  // events and only poll until the first reading lands.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    let timer = 0;
    let video = null;
    const read = () => {
      const v = el.querySelector("video");
      if (v && v !== video) {
        video = v;
        v.addEventListener("loadedmetadata", read);
        v.addEventListener("resize", read);
      }
      if (v && v.videoWidth && v.videoHeight) {
        const a = v.videoWidth / v.videoHeight;
        setAspect((prev) => (Math.abs(prev - a) > 0.01 ? a : prev));
        return;
      }
      timer = window.setTimeout(read, 400);
    };
    read();
    return () => {
      window.clearTimeout(timer);
      if (video) {
        video.removeEventListener("loadedmetadata", read);
        video.removeEventListener("resize", read);
      }
    };
  }, [children]);

  const c = crop && !isFullCrop(crop) ? crop : FULL;
  const { ox, oy, dw, dh } = imageRect(W, H, aspect);
  const cw = Math.max(1, c.w * dw);
  const ch = Math.max(1, c.h * dh);
  const s = aspect && W && H ? Math.min(W / cw, H / ch) : 1;
  const tx = W / 2 - s * (ox + c.x * dw + cw / 2);
  const ty = H / 2 - s * (oy + c.y * dh + ch / 2);

  useEffect(() => {
    if (!aspect) return;
    metaRef.current?.(aspect, (c.w * dw) / Math.max(1, c.h * dh));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspect, c.x, c.y, c.w, c.h, dw, dh]);

  const identity = c === FULL;

  return (
    <div ref={boxRef} className={`${className} overflow-hidden`} data-testid="share-crop-frame">
      <div
        className="absolute inset-0"
        style={
          identity
            ? undefined
            : {
                transform: `translate3d(${tx}px, ${ty}px, 0) scale(${s})`,
                transformOrigin: "0 0",
                transition: "transform 200ms ease-out",
                willChange: "transform",
              }
        }
        data-crop={identity ? "full" : `${c.x.toFixed(3)},${c.y.toFixed(3)},${c.w.toFixed(3)},${c.h.toFixed(3)}`}
      >
        {children}
      </div>
    </div>
  );
};

const HANDLES = [
  ["nw", "top-0 left-0", "-translate-x-1/2 -translate-y-1/2"],
  ["ne", "top-0 right-0", "translate-x-1/2 -translate-y-1/2"],
  ["sw", "bottom-0 left-0", "-translate-x-1/2 translate-y-1/2"],
  ["se", "bottom-0 right-0", "translate-x-1/2 translate-y-1/2"],
];

const MIN = 0.08;

/**
 * The host's crop tool. Sits on top of the *uncropped* share — students keep
 * seeing the old view until "Show this to everyone" is tapped. The controls
 * live in a fixed bar clear of the frame, so the corner handles are never
 * buried under a button.
 */
export const CropEditor = ({ initial, videoAspect, onApply, onCancel }) => {
  const boxRef = useRef(null);
  const { w: W, h: H } = useBoxSize(boxRef);
  const [c, setC] = useState(() => (initial && !isFullCrop(initial) ? initial : FULL));
  const [saved, setSaved] = useState(() => loadSavedCrops());
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const drag = useRef(null);
  const { ox, oy, dw, dh } = imageRect(W, H, videoAspect || 16 / 9);

  const preset = useCallback(
    (target) => {
      if (!target) return setC(FULL);
      // Biggest box of `target` aspect that fits inside the image, centred.
      const imgA = videoAspect || 16 / 9;
      let w = 1;
      let h = 1;
      if (target > imgA) h = imgA / target;
      else w = target / imgA;
      setC({ x: (1 - w) / 2, y: (1 - h) / 2, w, h });
    },
    [videoAspect]
  );

  const onDown = (e, mode) => {
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { mode, sx: e.clientX, sy: e.clientY, start: c };
  };

  const onMove = (e) => {
    const d = drag.current;
    if (!d || !dw || !dh) return;
    e.stopPropagation();
    const dx = (e.clientX - d.sx) / dw;
    const dy = (e.clientY - d.sy) / dh;
    const s = d.start;
    if (d.mode === "move") {
      setC({
        ...s,
        x: clamp01(Math.min(1 - s.w, s.x + dx)),
        y: clamp01(Math.min(1 - s.h, s.y + dy)),
      });
      return;
    }
    let { x, y, w, h } = s;
    const right = s.x + s.w;
    const bottom = s.y + s.h;
    if (d.mode.includes("w")) {
      x = clamp01(Math.min(right - MIN, s.x + dx));
      w = right - x;
    }
    if (d.mode.includes("e")) w = Math.min(1 - s.x, Math.max(MIN, s.w + dx));
    if (d.mode.includes("n")) {
      y = clamp01(Math.min(bottom - MIN, s.y + dy));
      h = bottom - y;
    }
    if (d.mode.includes("s")) h = Math.min(1 - s.y, Math.max(MIN, s.h + dy));
    setC({ x, y, w, h });
  };

  const onUp = (e) => {
    e.stopPropagation();
    drag.current = null;
  };

  const commitName = () => {
    const n = name.trim().slice(0, 14);
    if (!n) return setNaming(false);
    setSaved(addSavedCrop(n, c));
    setName("");
    setNaming(false);
  };

  const px = {
    left: ox + c.x * dw,
    top: oy + c.y * dh,
    width: c.w * dw,
    height: c.h * dh,
  };
  const stop = (e) => e.stopPropagation();

  return (
    <>
      <div
        ref={boxRef}
        className="absolute inset-0 z-30 touch-none"
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onDoubleClick={stop}
        data-testid="crop-editor"
      >
        {/* Everything outside the box is dimmed, so the host sees the students' view. */}
        <div
          className="absolute rounded-[2px] ring-2 ring-emerald-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.62)]"
          style={px}
          onPointerDown={(e) => onDown(e, "move")}
          data-testid="crop-box"
        >
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="border border-white/15" />
            ))}
          </div>
          {HANDLES.map(([mode, pos, shift]) => (
            <button
              key={mode}
              onPointerDown={(e) => onDown(e, mode)}
              className={`absolute ${pos} ${shift} w-9 h-9 grid place-items-center`}
              data-testid={`crop-handle-${mode}`}
              aria-label={`Drag ${mode} corner`}
            >
              <span className="w-4 h-4 rounded-sm bg-emerald-400 ring-2 ring-black/60" />
            </button>
          ))}
        </div>

        <div className="absolute inset-x-0 top-2 flex justify-center pointer-events-none">
          <div className="px-3 py-1.5 rounded-full bg-black/80 ring-1 ring-white/15 text-[11px] text-white/70">
            Drag the corners — students see this once you tap below
          </div>
        </div>
      </div>

      {/* Controls sit BELOW the frame, never over the corner handles. */}
      <div
        className="fixed left-0 right-0 bottom-[118px] [@media(max-height:520px)]:bottom-[74px] z-[70] px-3 flex flex-col items-center gap-2"
        onPointerDown={stop}
        onPointerMove={stop}
      >
        {saved.length > 0 && (
          <div className="max-w-full flex items-center gap-1.5 overflow-x-auto pb-0.5" data-testid="crop-saved-row">
            {saved.map((sc) => (
              <span
                key={sc.id}
                className="shrink-0 inline-flex items-center rounded-full bg-[#0b0b0b]/95 ring-1 ring-emerald-400/30"
              >
                <button
                  onClick={() => setC(sc.crop)}
                  className="h-8 pl-3 pr-2 text-[10.5px] uppercase tracking-[0.12em] font-semibold text-emerald-300"
                  data-testid={`crop-saved-${sc.name.toLowerCase()}`}
                >
                  {sc.name}
                </button>
                <button
                  onClick={() => setSaved(removeSavedCrop(sc.id))}
                  className="h-8 w-7 text-white/35 text-[13px] leading-none"
                  data-testid={`crop-saved-del-${sc.name.toLowerCase()}`}
                  aria-label={`Delete ${sc.name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {naming ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && commitName()}
              placeholder="Name it — Board, Slides…"
              maxLength={14}
              className="h-10 w-44 px-3 rounded-full bg-[#0b0b0b]/95 ring-1 ring-white/20 text-[12.5px] text-white placeholder:text-white/30 outline-none focus:ring-emerald-400/60"
              data-testid="crop-save-name"
            />
            <button
              onClick={commitName}
              className="h-10 px-4 rounded-full bg-emerald-400 text-black text-[10px] uppercase tracking-[0.14em] font-bold"
              data-testid="crop-save-confirm"
            >
              Save
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            {[
              ["Phone", 9 / 16],
              ["Wide", 16 / 9],
              ["Full", 0],
            ].map(([label, a]) => (
              <button
                key={label}
                onClick={() => preset(a)}
                className="h-9 px-3.5 rounded-full bg-[#0b0b0b]/90 ring-1 ring-white/20 text-white/75 text-[10px] uppercase tracking-[0.14em] font-semibold active:scale-95 transition-transform"
                data-testid={`crop-preset-${label.toLowerCase()}`}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => setNaming(true)}
              className="h-9 px-3.5 rounded-full bg-[#0b0b0b]/90 ring-1 ring-emerald-400/40 text-emerald-300 text-[10px] uppercase tracking-[0.14em] font-semibold"
              data-testid="crop-save-btn"
            >
              Save this
            </button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="h-11 px-5 rounded-full bg-[#0b0b0b]/95 ring-1 ring-white/20 text-white/70 text-[10px] uppercase tracking-[0.16em] font-semibold"
            data-testid="crop-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => onApply(isFullCrop(c) ? null : c)}
            className="h-11 px-6 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 text-black text-[10.5px] uppercase tracking-[0.16em] font-bold shadow-[0_14px_34px_-12px_rgba(16,185,129,0.7)]"
            data-testid="crop-apply"
          >
            Show this to everyone
          </button>
        </div>
      </div>
    </>
  );
};
